import { generateTrackingId } from "client/Misc/utils";
import { forwardRef, PropsWithChildren, ReactElement, Ref } from "react";
import { ActionList } from "../ActionList/ActionList";
import { ActionListBox } from "../ActionListBox/ActionListBox";
import {
	AbsoluteCoordinate,
	DropOver,
	DropOverAlignmentValue,
	DropOverProps,
	RectangleTargetAlignment
} from "../DropOver/DropOver";

export type DropOverActionListProps = Pick<
	DropOverProps,
	"outsideClickIgnoreRefs" | "shouldStayOpenOnScroll"
> & {
	isHeightConstrainted?: boolean;
	width?: number | "max";
	target: HTMLElement;
	targetAlignment?:
		| DropOverAlignmentValue
		| AbsoluteCoordinate
		| RectangleTargetAlignment;
	contentAlignment?: DropOverAlignmentValue | undefined;
	bubbleClick?: boolean;
	onClose?(): void;
	hasResponsiveDropOver?: boolean;
	trackingId?: string;
	closeOnMouseDown?: boolean;
	bubbleContextMenu?: boolean;
	hasBusyIndicator?: boolean;
	menuTitle: string;
	shouldCloseWhenWindowLoosesFocus?: boolean;
};

export const DropOverActionList = forwardRef<HTMLDivElement, PropsWithChildren<DropOverActionListProps>>(
	(
		props: PropsWithChildren<DropOverActionListProps>,
		ref: Ref<HTMLDivElement>
	): ReactElement => {
	const {
		isHeightConstrainted,
		width,
		children,
		target,
		targetAlignment,
		contentAlignment,
		bubbleClick,
		onClose,
		hasResponsiveDropOver = true,
		trackingId,
		outsideClickIgnoreRefs,
		closeOnMouseDown,
		bubbleContextMenu = true,
		hasBusyIndicator,
		menuTitle,
		shouldCloseWhenWindowLoosesFocus,
		shouldStayOpenOnScroll
	} = props;

	return (
		<DropOver
			target={target}
			targetAlignment={targetAlignment}
			contentAlignment={contentAlignment}
			bubbleClick={bubbleClick}
			onClose={onClose}
			hasResponsiveDropOver={hasResponsiveDropOver}
			ref={ref}
			outsideClickIgnoreRefs={outsideClickIgnoreRefs}
			closeOnMouseDown={closeOnMouseDown}
			bubbleContextMenu={bubbleContextMenu}
			shouldCloseWhenWindowLoosesFocus={shouldCloseWhenWindowLoosesFocus}
			shouldStayOpenOnScroll={shouldStayOpenOnScroll}
		>
			<ActionListBox
				isHeightConstrainted={isHeightConstrainted}
				responsiveMode={{
					kind: hasResponsiveDropOver
						? "auto"
						: "never"
				}}
				width={typeof width === "number" ? width : undefined}
				hasBusyIndicator={hasBusyIndicator}
				menuTitle={menuTitle}
				trackingId={generateTrackingId(trackingId, "__wrapper")}
			>
				<ActionList
					width={typeof width === "number" ? "fill" : width}
					trackingId={trackingId}
				>
					{children}
				</ActionList>
			</ActionListBox>
		</DropOver>
	);
});
DropOverActionList.displayName = "DropOverActionList";
